import { isFromBreakpoint } from '~/breakpoints';
import { classes } from '~/helpers';
import { computePosition, offset, flip, autoUpdate } from 'floating-ui';
import { HTMLShopifySectionElement } from '~/html-shopify-section-element';
import { HTMLCustomElement } from '~/html-custom-element';

customElements.define(
  'product-hotspots',
  class ProductHotspots extends HTMLShopifySectionElement {
    connectedCallback() {
      if (super.connectedCallback) super.connectedCallback();

      this.autoUpdateInstances = [];

      if (this.targets.product) {
        this.targets.product.all.forEach((product, index) => {
          const button =
            this.targets.button &&
            this.targets.button.all.find(({ dataset }) => dataset.block === product.dataset.block);

          this.autoUpdateInstances.push(
            autoUpdate(button, product, () => this.setBlockPosition(product.dataset.block))
          );
        });
      }

      this.targets.button && this.on('click', this.targets.button.all, this.handleButtonEvent.bind(this));
    }

    /**
     * Handles the button click event.
     * @param {MouseEvent} event - The event payload.
     */
    handleButtonEvent(event) {
      this.setSelectedHotspot(event.currentTarget.dataset.block);
    }

    /**
     * Sets the selected hotspot.
     * @param {String} id - The hotspot identifier.
     */
    setSelectedHotspot(id) {
      [
        ...((this.targets.button && this.targets.button.all) ?? []),
        ...((this.targets.product && this.targets.product.all) ?? []),
      ].forEach((element) => {
        element.classList.toggle(classes.active, element.dataset.block === id);
      });

      if (id) {
        this.dispatchEvent(new CustomEvent('active'));
      }
    }

    /**
     * Positions a block's product.
     * @param {String} id - The block id.
     */
    setBlockPosition(id) {
      const product = this.targets.product && this.targets.product.all.find(({ dataset }) => dataset.block === id);
      const button = this.targets.button && this.targets.button.all.find(({ dataset }) => dataset.block === id);

      if (!button || !product) return;

      if (isFromBreakpoint('medium')) {
        computePosition(button, product, {
          placement: 'right-start',
          middleware: [
            offset(10),
            flip({
              padding: 10,
            }),
          ],
        }).then(({ x, y }) => {
          Object.assign(product.style, {
            left: `${x}px`,
            top: `${y}px`,
          });
        });
        return;
      }

      product.style.removeProperty('left');
      product.style.removeProperty('top');
    }

    onBlockSelect(event) {
      this.setSelectedHotspot(event.detail.blockId);
    }

    disconnectedCallback() {
      if (super.disconnectedCallback) super.disconnectedCallback();

      this.autoUpdateInstances.forEach((remove) => remove());
      this.autoUpdateInstances = [];
    }
  }
);

class ProductHotspotsGroup extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    const defaultBlock = [...this.querySelectorAll('product-hotspots')].find((element) =>
      element.hasAttribute('default-hotspot')
    );

    if (defaultBlock) {
      defaultBlock.setSelectedHotspot(defaultBlock.getAttribute('default-hotspot'));
    }

    this.on('active', [...this.querySelectorAll('product-hotspots')], (event) => {
      this.querySelectorAll('product-hotspots').forEach((element) => {
        if (element === event.target) {
          return;
        }

        element.setSelectedHotspot(null);
      });
    });
  }
}

if (!customElements.get('product-hotspots')) customElements.define('product-hotspots', ProductHotspots);
if (!customElements.get('product-hotspots-group'))
  customElements.define('product-hotspots-group', ProductHotspotsGroup);
